import type { AppendMessage } from "@assistant-ui/react";
import { AssistantRuntimeProvider, useExternalStoreRuntime } from "@assistant-ui/react";
import { type ReactNode, Suspense, useCallback, useMemo } from "react";
import { useSalonId } from "@/lib/store/salon";
import { getAttachmentAdapter } from "../adapters/backend-attachment-adapter";
import { OpenAITTSAdapter } from "../adapters/openai-tts-adapter";
import { AiThreadContext } from "../contexts/ai-thread-context";
import { useAiChatStreaming } from "../hooks/use-ai-chat-streaming";
import { useViewContextTool } from "../hooks/use-view-context-tool";
import { useAiChatStore } from "../stores/ai-chat.store";

interface AIChatRuntimeProviderProps {
  children: ReactNode;
}

/**
 * Runtime provider for AI Chat using ExternalStoreRuntime
 *
 * Tools are managed via the client-tools-store and registered using useClientTool hook.
 * This provider handles the runtime setup and message streaming.
 */

/**
 * Inner component that registers global tools
 */
function ToolRegistration({ children }: { children: ReactNode }) {
  // Register global tools
  useViewContextTool();

  return <>{children}</>;
}

export function AIChatRuntimeProvider({ children }: AIChatRuntimeProviderProps) {
  const salonId = useSalonId();

  // Connect to Zustand store to get state
  const { messages, isRunning, threadId, resetThread } = useAiChatStore();

  // Initialize streaming hook (tools come from store)
  const { streamNewMessage, streamReload, streamHistory, cancelStream } = useAiChatStreaming();

  /**
   * Handle onNew from ExternalStoreRuntime
   * Extracts attachmentIds from message and calls streamNewMessage
   *
   * Note: View context is automatically included in streamNewMessage via useAiChatStreaming
   */
  const onNew = useCallback(
    async (message: AppendMessage) => {
      // Extract attachment IDs from message.attachments
      const attachmentIds: string[] = [];

      if (message.attachments && Array.isArray(message.attachments)) {
        for (const attachment of message.attachments) {
          if (attachment.id) {
            attachmentIds.push(attachment.id);
          }
        }
      }

      // Call streamNewMessage with the message and attachmentIds
      await streamNewMessage({ message, attachmentIds });
    },
    [streamNewMessage]
  );

  /**
   * Handle onReload from ExternalStoreRuntime
   */
  const onReload = useCallback(
    async (parentId: string | null) => {
      await streamReload(parentId);
    },
    [streamReload]
  );

  /**
   * Handle onCancel from ExternalStoreRuntime
   */
  const onCancel = useCallback(async () => {
    await cancelStream();
  }, [cancelStream]);

  /**
   * Load thread wrapper for AiThreadContext
   */
  const loadThread = useCallback(
    async (threadIdToLoad: string) => {
      await streamHistory(threadIdToLoad);
    },
    [streamHistory]
  );

  // Create speech adapter (singleton per salon)
  const speechAdapter = useMemo(() => {
    return new OpenAITTSAdapter({ salonId: salonId });
  }, [salonId]);

  // Get cached attachment adapter for current salon
  const attachmentAdapter = useMemo(() => {
    if (!salonId) return undefined;
    return getAttachmentAdapter(salonId);
  }, [salonId]);

  // Stabilize adapters object to prevent recreation
  const adapters = useMemo(
    () => ({
      ...(speechAdapter && { speech: speechAdapter }),
      ...(attachmentAdapter && { attachments: attachmentAdapter }),
    }),
    [speechAdapter, attachmentAdapter]
  );

  const runtime = useExternalStoreRuntime({
    messages,
    isRunning,
    onNew,
    onReload,
    onCancel,
    convertMessage: (message) => message, // Identity function - already in correct format
    adapters,
  });

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      }
    >
      <AiThreadContext.Provider
        value={{
          currentThreadId: threadId,
          resetThread,
          loadThread,
        }}
      >
        <AssistantRuntimeProvider runtime={runtime}>
          <ToolRegistration>{children}</ToolRegistration>
        </AssistantRuntimeProvider>
      </AiThreadContext.Provider>
    </Suspense>
  );
}
